import { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { fetchLabKPIMetrics } from "../../../API_Service/Dashboard_Slice";
import { companyID, KPICard } from "../../../Common/UIComponents";
import { useDashboardSignalR } from "../../../Common/useDashboardSignalR";

const LabKPICards = () => {
  const dispatch = useDispatch();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const loadStats = useCallback(() => {
    console.log("🔍 LabKPICards - companyID from localStorage:", companyID);
    if (!companyID) {
      console.warn("⚠️ LabKPICards - Company ID not found in localStorage");
      setError("Company ID not found");
      setIsLoading(false);
      return;
    }

    console.log("✅ LabKPICards - Fetching data for date:", selectedDate);
    setIsLoading(true);
    dispatch(fetchLabKPIMetrics({ companyID, date: selectedDate }))
      .then((result) => {
        if (result.payload && result.payload.length > 0) {
          setStats(result.payload[0]);
          setError(null);
        } else if (result.payload && !Array.isArray(result.payload)) {
          setStats(result.payload);
        }
      })
      .catch((err) => {
        setError(err?.message || "Failed to load Lab KPI cards");
      })
      .finally(() => setIsLoading(false));
  }, [dispatch, selectedDate]);

  useEffect(() => { loadStats(); }, [loadStats]);

  useDashboardSignalR(loadStats);

  const cards = stats
    ? [
        {
          icon: "domain",
          label: "Total Labs",
          value: stats.TotalLabs,
          iconColor: "text-blue-600",
          gradientFrom: "from-blue-500",
          gradientTo: "to-blue-600",
        },
        {
          icon: "check_circle",
          label: "Occupied Labs",
          value: stats.OccupiedLabs,
          iconColor: "text-green-600",
          gradientFrom: "from-green-500",
          gradientTo: "to-emerald-600",
        },
        {
          icon: "add_circle",
          label: "Available Labs",
          value: stats.AvailableLabs,
          iconColor: "text-purple-600",
          gradientFrom: "from-purple-500",
          gradientTo: "to-purple-600",
        },
        {
          icon: "warning",
          label: "Breakdown Labs",
          value: stats.BreakdownLabs,
          iconColor: "text-orange-600",
          gradientFrom: "from-orange-500",
          gradientTo: "to-red-600",
        },
        {
          icon: "event",
          label: "Today's Bookings",
          value: stats.TodayBookings,
          iconColor: "text-indigo-600",
          gradientFrom: "from-indigo-500",
          gradientTo: "to-indigo-600",
        },
        {
          icon: "pending_actions",
          label: "Total Pending Approvals",
          value: stats.PendingApprovals,
          iconColor: "text-red-600",
          gradientFrom: "from-red-500",
          gradientTo: "to-pink-600",
        },
        {
          icon: "trending_up",
          label: "Today's Utilization",
          value: `${(stats.TodayUtilization || 0).toFixed(2)}%`,
          iconColor: "text-cyan-600",
          gradientFrom: "from-cyan-500",
          gradientTo: "to-cyan-600",
        },
      ]
    : [];

  if (error) {
    return (
      <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-600">Error loading Lab KPI cards: {error}</p>
      </div>
    );
  }

  const handlePreviousDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const handleNextDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
  };

  const today = new Date().toISOString().split('T')[0];
  const isToday = selectedDate === today;

  return (
    <div className="mb-8">
      {/* Simple Date Picker */}
      <div className="mb-4 flex items-center justify-end gap-2">
        <button
          onClick={handlePreviousDate}
          disabled={isToday}
          className={`p-1.5 rounded-lg transition-colors ${
            isToday ? "opacity-40 cursor-not-allowed" : "hover:bg-blue-100"
          }`}
          title={isToday ? "Cannot go before today" : "Previous Day"}
        >
          <span className="material-symbols-outlined text-blue-600">chevron_left</span>
        </button>

        <div className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg bg-white">
          <span className="material-symbols-outlined text-blue-600">calendar_today</span>
          <input
            type="date"
            value={selectedDate}
            min={today}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="text-sm font-medium text-gray-700 focus:outline-none bg-transparent cursor-pointer"
          />
        </div>

        <button
          onClick={handleNextDate}
          className="p-1.5 hover:bg-blue-100 rounded-lg transition-colors"
          title="Next Day"
        >
          <span className="material-symbols-outlined text-blue-600">chevron_right</span>
        </button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => (
          <KPICard
            key={index}
            icon={card.icon}
            label={card.label}
            value={card.value}
            iconColor={card.iconColor}
            gradientFrom={card.gradientFrom}
            gradientTo={card.gradientTo}
            isLoading={isLoading}
          />
        ))}
        {isLoading && cards.length === 0 && (
          <>
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 p-6 animate-pulse">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 rounded-t-2xl"></div>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="h-3 bg-gray-200 rounded w-2/3 mb-3"></div>
                    <div className="h-10 bg-gray-200 rounded w-1/2"></div>
                  </div>
                  <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default LabKPICards;
